/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * boardApi — the leaderboard's reads.
 *
 * PUBLIC ON PURPOSE. The board is a results list for a public event, so it is
 * read with the application's own credentials and no personal login, through
 * `application/public/table/fetch/`. A visitor sees the field; only the "your
 * result" read below needs a signed-in session.
 *
 * TWO QUIRKS OF THAT ENDPOINT, both worked around here:
 *
 *  1. It does not sort. The SQL it builds is SELECT * ... WHERE ... with no
 *     ORDER BY, so ordering happens in the browser (see sortRows in boardModel).
 *     Its row ceiling is 25,000, not the 100 that applies to page-builder
 *     pages, so a whole field comfortably fits in one read.
 *  2. Its search term is read from the QUERY STRING, not the body, unlike every
 *     other parameter.
 */

import envConfig from '../../../config/envConfig';
import { RESULT_TABLE, REGISTRATION_TABLE } from './resultsApi';

/** One read covers a whole field; nothing we run approaches the 25,000 ceiling. */
export const BOARD_PAGE_SIZE = 500;

function scope() {
  return {
    organization_id: Number(envConfig.organizationId),
    application_id: Number(envConfig.applicationId),
  };
}

function rowsOf(payload) {
  const rows = Array.isArray(payload) ? payload : (payload?.records || payload?.results || []);
  const count = typeof payload?.count === 'number' ? payload.count : rows.length;
  return { rows, count };
}

/**
 * One page of a race's results, filtered to the statuses of one tab.
 *
 * `statuses` is a list because a tab can cover more than one: Did not finish
 * holds DID_NOT_FINISH and OTHER, since somebody who pulled out at 15 km did
 * not "not participate".
 */
export async function fetchRaceResults(api, {
  eventActivityId, statuses, page = 1, pageSize = BOARD_PAGE_SIZE, search, signal,
} = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return { rows: [], count: 0 };
  const filters = [
    { column_key: 'event_activity_id', filter_type: 'equals', column_value: eventActivityId },
  ];
  if (statuses && statuses.length) {
    filters.push({ column_key: 'status', filter_type: 'in', column_value: statuses });
  }
  // PAGING RIDES ON THE QUERY STRING, AND HAS TO.
  //
  // This endpoint paginates with DRF's own paginator, which reads `page` and
  // `page_size` from the QUERY STRING and nothing else. Sent in the body they
  // are silently ignored and every request comes back as page 1 of 10 — which
  // is exactly what the board did: a 67-finisher race reported "Showing 1–10
  // of 10" while its own header said 67, and asking for 500 changed nothing.
  // Probed 2026-11-03. They are still sent in the body as well, harmlessly, so
  // that a caller reading this request can see what was asked for.
  // encodeURIComponent, not URLSearchParams: the latter writes a space as '+',
  // and the search term's encoding is somebody else's settled decision — this
  // change is about paging and should leave it exactly as it was.
  const query = `?page=${page}&page_size=${pageSize}`
    + (search ? `&search=${encodeURIComponent(search)}` : '');
  const resp = await api.publicApiCall(`/arcee/application/public/table/fetch/${query}`, {
    method: 'POST',
    signal,
    body: JSON.stringify({ ...scope(), table_name: RESULT_TABLE, filters, page, page_size: pageSize }),
  });
  if (!resp.ok) throw new Error('Could not load the results for this race.');
  return rowsOf(await resp.json());
}

/**
 * Rows per page for a read that has to see EVERY result, not a screenful.
 *
 * 2000, not the board's 500 and not the endpoint's 25000 ceiling. The reason
 * is in how this endpoint paginates: it runs `SELECT *` with the filters, reads
 * the WHOLE matching set, expands every relation on it, and only then slices a
 * page out in Python. So each request already costs the entire race whatever
 * page size is asked for, and a small page is not a cheap page — it is the same
 * full scan, repeated. Ten thousand results at 200 a time is fifty full scans.
 *
 * The ceiling is left unused on purpose: the server work is the same either
 * way, but one response carrying every column of every row is the one that
 * times out on event wifi, with no partial progress to show while it is in
 * flight. 2000 is five ordinary round trips for a ten-thousand-runner race.
 */
export const FULL_READ_PAGE_SIZE = 2000;

/**
 * Every result for a race, however many pages that takes.
 *
 * WHY THIS EXISTS. Anything that must reason about the WHOLE field — above
 * all, "which registrations already have a result?" before an import — cannot
 * use a saved query: those run through the analytics endpoint, which has no
 * paging at all and caps every answer at 500 rows. A race with more than 500
 * results then reports half of them as having none, and the import tries to
 * create a second result for people who already have one.
 *
 * It reads through the PUBLIC table endpoint, like the board, which is what
 * makes it work for an organizer: that call carries the client key and no
 * session, so there is no signed-in caller for row-level security to narrow
 * against. The authenticated table read of `event_result` IS refused to an
 * organizer — that refusal is real and is why the saved query was written —
 * but it is a different endpoint from this one.
 *
 * STOPS ON NO NEW ROWS, not on a page count. This endpoint has ignored `page`
 * before (probed 2026-11-02, fixed by moving paging onto the query string), and
 * a loop that trusts `page` would spin for ever if that ever regressed. Tracking
 * ids means a page that repeats the last one ends the loop instead.
 */
export async function fetchAllRaceResults(api, {
  eventActivityId, pageSize = FULL_READ_PAGE_SIZE, signal,
} = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return { rows: [], count: 0 };

  const all = [];
  const seen = new Set();
  let page = 1;
  let reported = 0;

  for (;;) {
    // eslint-disable-next-line no-await-in-loop
    const answer = await fetchRaceResults(api, { eventActivityId, page, pageSize, signal });
    const rows = answer.rows || [];
    reported = Number(answer.count) || reported;

    let fresh = 0;
    rows.forEach((row) => {
      const key = String(row.id);
      if (seen.has(key)) return;
      seen.add(key);
      all.push(row);
      fresh += 1;
    });

    // Nothing new on this page: either the race is exhausted, or the endpoint
    // handed back a page it has already given us. Both mean stop.
    if (fresh === 0) break;
    if (reported && all.length >= reported) break;
    page += 1;
  }

  return { rows: all, count: reported || all.length };
}

/**
 * How many results sit behind each tab.
 *
 * Every read answers with a total, so asking for a single row per status buys
 * the count without carrying the rows. That is why the tab counts need no
 * stored query of their own.
 */
export async function fetchStatusCounts(api, { eventActivityId, tabs, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return {};
  const entries = await Promise.all((tabs || []).map(async (tab) => {
    try {
      const { count } = await fetchRaceResults(api, {
        eventActivityId, statuses: tab.statuses, page: 1, pageSize: 1, signal,
      });
      return [tab.key, count];
    } catch (e) {
      // A tab whose count cannot be read shows no number rather than failing
      // the whole board.
      return [tab.key, null];
    }
  }));
  return Object.fromEntries(entries);
}

/**
 * The registrations this viewer holds for one race.
 *
 * WHY THE BOARD NEEDS THESE. "Which of these results is mine" used to be
 * answered with `created_by_id` — who wrote the result row. That was right
 * while athletes attached their own runs, and became wrong the moment
 * ORGANIZERS started attaching on their behalf: the creator is then the
 * organizer, so the athlete found nothing of theirs on the board and the
 * organizer would have seen every row they touched badged as their own. The
 * question that survives both is "is this result against MY entry".
 *
 * `event_registration` is private, and the batch read endpoint does NOT apply
 * row-level security — the `created_by_id` here is what scopes the read, so it
 * must always come from the session and never from a URL. The server checks it
 * too: probed 2026-11-03 signed in as one athlete, asking for another's
 * registrations is refused outright with `not_permitted`, not answered with
 * their rows. Re-probe with playwright-local/scripts/athlohive-my-registrations.js
 * after any change here.
 *
 * Returns null — not an empty list — when the question cannot be answered, so
 * the caller can tell "not registered" from "could not ask" and fall back
 * rather than quietly deciding nothing is yours.
 */
export async function fetchMyRegistrationIds(api, { userId, eventActivityId, signal } = {}) {
  if (!userId || eventActivityId === null || eventActivityId === undefined) return null;
  try {
    const resp = await api.apiCall('/arcee/application/table/read/records/', {
      method: 'POST',
      signal,
      body: JSON.stringify({
        ...scope(),
        operations: [{
          ref: 'mine',
          table: REGISTRATION_TABLE,
          fields: ['id'],
          where: { event_activity_id: eventActivityId, created_by_id: userId },
          limit: 50,
        }],
      }),
    });
    // 403 is the expected answer on an environment that has not granted
    // participants read on this table. Not an error worth showing anybody.
    if (!resp.ok) return null;
    const body = await resp.json().catch(() => null);
    const block = body && body.results && body.results.mine;
    if (!block || block.status === 'error' || !Array.isArray(block.data)) return null;
    return block.data.map((r) => String(r.id));
  } catch (e) {
    return null;
  }
}

/**
 * The signed-in viewer's own result for this race, or null.
 *
 * Read with the session rather than matched by name on the public rows: two
 * athletes can share a name, and "which row is me" must not be a guess.
 *
 * Matched on REGISTRATION first — see fetchMyRegistrationIds for why. The
 * creator match is kept as a fallback for the case where the registrations
 * cannot be read at all: it still finds a run the athlete attached themselves,
 * which is exactly what this did before, so an environment without that
 * permission is no worse off than it is today.
 */
export async function fetchMyResultForRace(api, { userId, eventActivityId, signal } = {}) {
  if (!userId || eventActivityId === null || eventActivityId === undefined) return null;

  const byFilters = async (filters) => {
    const resp = await api.apiCall('/arcee/application/table/fetch/', {
      method: 'POST',
      signal,
      body: JSON.stringify({
        ...scope(), table_name: RESULT_TABLE, filters, page: 1, page_size: 1,
      }),
    });
    if (!resp.ok) return null;
    const { rows } = rowsOf(await resp.json());
    return rows[0] || null;
  };

  const race = { column_key: 'event_activity_id', filter_type: 'equals', column_value: eventActivityId };

  const registrationIds = await fetchMyRegistrationIds(api, { userId, eventActivityId, signal });

  // ANSWERED — so the answer stands, including "none of them".
  //
  // The creator fallback below is only correct while the creator IS the
  // athlete. Since organizers attach on everyone's behalf, every row of a race
  // carries the ORGANIZER as its creator, so an organizer reading a board they
  // do not run in matched the first result in the race and it was badged as
  // theirs — seen 2026-11-03 on a 67-entrant race, where the organizer was
  // labelled YOU against somebody else's second place. Asking and being told
  // "you have no entry here" is a real answer and must end the question.
  if (registrationIds) {
    if (!registrationIds.length) return null;
    return byFilters([
      race,
      { column_key: 'registration_id', filter_type: 'in', column_value: registrationIds },
    ]);
  }

  // Only now: the registrations could not be read at all. The old question is
  // still the best available, and on such an environment it is no worse than
  // what this did before.
  return byFilters([race, { column_key: 'created_by_id', filter_type: 'equals', column_value: userId }]);
}

/** Most a single board read will pull back, however big the field is. */
export const BOARD_MAX_ROWS = 5000;

/**
 * A whole tab's rows, so the browser can sort the real ranking rather than
 * sorting one page of it.
 *
 * Sorting a page at a time would be wrong, not just partial: the 101st fastest
 * runner would never be compared with the first hundred. So the first read
 * reports how many there are, and a second asks for the rest only when needed —
 * one call for an ordinary race, two for a big one.
 */
export async function fetchWholeTab(api, { eventActivityId, statuses, search, signal } = {}) {
  const first = await fetchRaceResults(api, {
    eventActivityId, statuses, search, page: 1, pageSize: BOARD_PAGE_SIZE, signal,
  });
  if (first.count <= first.rows.length) return { ...first, truncated: false };

  const wanted = Math.min(first.count, BOARD_MAX_ROWS);
  const full = await fetchRaceResults(api, {
    eventActivityId, statuses, search, page: 1, pageSize: wanted, signal,
  });
  return { ...full, truncated: full.count > full.rows.length };
}

/** The saved queries the board reads, seeded by `seed_analytics_queries`. */
export const FIELD_SUMMARY_QUERY = 'event_result_field_summary';
export const FINISH_BANDS_QUERY = 'event_result_finish_bands';

/** An analytics answer ({columns, rows}) as a list of objects keyed by column. */
export function recordsOf(payload) {
  const columns = (payload?.columns || []).map((c) => c.name);
  return (payload?.rows || []).map((row) => Object.fromEntries(columns.map((c, i) => [c, row[i]])));
}

async function runPublicQuery(api, name, params, signal) {
  const query = new URLSearchParams(params).toString();
  const resp = await api.publicApiCall(
    `/arcee/public/${envConfig.organizationId}/${envConfig.applicationId}/analytics/${name}/run/?${query}`,
    { method: 'GET', signal },
  );
  if (!resp.ok) throw new Error(`The ${name} query is not available.`);
  return recordsOf(await resp.json());
}

/**
 * Every tab count, the median and the two extremes, in ONE read.
 *
 * The counts could be had from five separate reads, since each read answers
 * with a total — but the median could not: it needs every finish time sorted,
 * not the first page of them. One saved query carries both, so the board asks
 * once rather than six times.
 */
export async function fetchFieldSummary(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return null;
  const rows = await runPublicQuery(api, FIELD_SUMMARY_QUERY, { event_activity_id: eventActivityId }, signal);
  return rows[0] || null;
}

/** The finish-time histogram, in five-minute bands. */
export async function fetchFinishBands(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return [];
  return runPublicQuery(api, FINISH_BANDS_QUERY, { event_activity_id: eventActivityId }, signal);
}

/** A summary row reshaped into the per-tab counts the tab strip draws. */
export function countsFromSummary(summary) {
  if (!summary) return null;
  return {
    completed: Number(summary.completed || 0),
    in_progress: Number(summary.in_progress || 0),
    did_not_finish: Number(summary.did_not_finish || 0),
    not_participated: Number(summary.not_participated || 0),
    injured: Number(summary.injured || 0),
  };
}

/** The published winners for a race. Public: an award is an announcement. */
export async function fetchPodium(api, { eventActivityId, signal } = {}) {
  if (eventActivityId === null || eventActivityId === undefined) return [];
  const resp = await api.publicApiCall('/arcee/application/public/table/fetch/', {
    method: 'POST',
    signal,
    body: JSON.stringify({
      ...scope(),
      table_name: 'event_activity_result_award_summary',
      filters: [{ column_key: 'event_activity_id', filter_type: 'equals', column_value: eventActivityId }],
      page: 1,
      page_size: 50,
    }),
  });
  if (!resp.ok) return [];
  const { rows } = rowsOf(await resp.json());
  // Lowest place first: 1st, then 2nd, then 3rd.
  return [...rows].sort((a, b) => Number(a.position) - Number(b.position));
}
